import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, fileUrl } from "@/api";
import { useI18n } from "@/contexts/I18nContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import LanguageToggle from "@/components/LanguageToggle";
import useQueueSocket from "@/hooks/useQueueSocket";
import { ArrowLeft, Stethoscope, Building2, Clock, MapPin, IndianRupee, Activity, AlertTriangle, ShieldCheck, CalendarDays } from "lucide-react";
import NmcBadge from "@/components/NmcBadge";
import PublicReviewsWall from "@/components/PublicReviewsWall";
import { toast } from "sonner";

export default function PatientDoctorProfile() {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [schedules, setSchedules] = useState(null);
  const liveSnap = useQueueSocket(doctorId);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/doctors/${doctorId}`);
        setData(data);
        try {
          const sres = await axios.get(`${API}/public/doctors/${doctorId}/schedules`);
          setSchedules(sres.data);
        } catch { /* schedules optional */ }
      } finally { setLoading(false); }
    })();
  }, [doctorId]);

  if (loading) return <div className="p-8 text-slate-500">{t("loading")}</div>;
  if (!data) return null;

  const { doctor, org } = data;
  const queue = liveSnap || data.queue;
  const onLeave = doctor.availability === "on_leave";
  const yourPositionIfBookNow = queue ? queue.waiting_count + (queue.current_token ? 1 : 0) + 1 : 1;
  const etaIfBookNow = (yourPositionIfBookNow - 1) * (queue?.avg_diagnosis_time || doctor.avg_diagnosis_time || 10);

  return (
    <div className="min-h-screen bg-slate-50 bg-grain" data-testid="patient-doctor-profile-page">
      <header className="px-4 sm:px-8 py-4 flex items-center justify-between bg-white border-b sticky top-0 z-10">
        <Link to="/patient" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900" data-testid="back-patient-home">
          <ArrowLeft className="w-4 h-4" /> {t("back")}
        </Link>
        <LanguageToggle />
      </header>

      <main className="px-4 sm:px-8 py-6 sm:py-10 max-w-4xl mx-auto space-y-5">
        {/* Doctor card */}
        <Card className="medical-card p-5 sm:p-7">
          <div className="flex items-start gap-4 sm:gap-6">
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl overflow-hidden bg-gradient-to-br from-sky-100 to-emerald-100 flex items-center justify-center shrink-0">
              {doctor.profile_pic ? <img src={fileUrl(doctor.profile_pic)} alt="" className="w-full h-full object-cover" /> :
                <Stethoscope className="w-10 h-10 text-sky-500" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900" data-testid="patient-profile-name">{doctor.name}</h1>
                <NmcBadge doctor={doctor} size="md" testid="patient-profile-nmc-badge" />
              </div>
              <p className="text-sm text-slate-500 mt-1">
                {lang === "hi" && doctor.specialization_hi ? doctor.specialization_hi : doctor.specialization_en}
                {doctor.qualification ? ` · ${doctor.qualification}` : ""}
              </p>
              {org && (
                <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                  <Building2 className="w-3 h-3" /> {org.name}{org.city ? `, ${org.city}` : ""}
                </p>
              )}
              {doctor.trust_score > 0 && (
                <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-200" data-testid="patient-profile-trust-score">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  Trusted by {doctor.trust_score} doctor{doctor.trust_score === 1 ? "" : "s"} · {doctor.trust_score} referral{doctor.trust_score === 1 ? "" : "s"}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 text-sm">
            <Stat icon={IndianRupee} label={t("consultation_fee")} value={`₹${doctor.consultation_fee}`} />
            <Stat icon={MapPin} label={t("room_cabin")} value={doctor.room_number || "—"} />
            <Stat icon={Clock} label={t("avg_diag_time")} value={`${doctor.avg_diagnosis_time} ${t("minutes")}`} />
            <Stat icon={Activity} label={t("working_hours")} value={doctor.working_hours || "—"} />
          </div>
        </Card>

        {/* Live queue */}
        <Card className="medical-card p-5 sm:p-6" data-testid="patient-queue-card">
          <div className="flex items-center gap-2 mb-4">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
            </span>
            <h2 className="font-heading text-lg font-semibold text-slate-900">{t("live")} {t("current_token")}</h2>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Tile label={t("current_token")} value={queue?.current_token ? `#${queue.current_token}` : "—"} accent="sky" testid="tile-current" />
            <Tile label={t("waiting")} value={queue?.waiting_count ?? 0} accent="amber" testid="tile-waiting" />
            <Tile label={t("completed")} value={queue?.completed_count ?? 0} accent="emerald" testid="tile-completed" />
          </div>
          {!onLeave && (
            <div className="mt-5 rounded-lg bg-slate-50 border border-slate-200 p-4 text-sm">
              <p className="text-slate-700">
                {t("your_position")}: <span className="font-semibold">#{yourPositionIfBookNow}</span>
                {" · "}
                {t("estimated_wait")}: <span className="font-semibold text-sky-600">{etaIfBookNow} {t("minutes")}</span>
              </p>
              {(queue?.online_ahead != null) && (
                <p className="text-xs text-slate-500 mt-1" data-testid="hybrid-queue-mix">
                  {queue.online_ahead} Online + {queue.walk_in_ahead} Walk-In patient(s) ahead of you
                </p>
              )}
              {queue?.delay_minutes > 0 && (
                <p className="text-xs text-amber-700 mt-1" data-testid="delay-banner">
                  Doctor announced ~{queue.delay_minutes} min delay. Add this to ETA.
                </p>
              )}
            </div>
          )}
        </Card>

        {/* Public reviews wall (renders nothing until reviews exist) */}
        <PublicReviewsWall doctorId={doctorId} />

        {/* Multi-clinic schedule timeline */}
        {schedules && schedules.schedules && schedules.schedules.length > 0 && (
          <Card className="medical-card p-5 sm:p-6" data-testid="patient-doctor-schedules">
            <div className="flex items-center gap-2 mb-3">
              <CalendarDays className="w-4 h-4 text-sky-600" />
              <h2 className="font-heading text-lg font-semibold text-slate-900">Weekly schedule</h2>
            </div>
            <ScheduleTimeline schedules={schedules.schedules} orgs={schedules.orgs || {}} />
          </Card>
        )}

        {/* Book button */}
        {onLeave ? (
          <Card className="medical-card p-5 border-amber-200 bg-amber-50 flex items-center gap-3" data-testid="doctor-on-leave-banner">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <p className="text-sm text-amber-800">{t("on_leave")}</p>
          </Card>
        ) : (
          <BookDialog doctor={doctor} org={org} onBooked={(appt) => navigate(`/patient/slip/${appt.id}`)} />
        )}
      </main>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="flex items-center gap-1 text-xs text-slate-500"><Icon className="w-3 h-3" /> {label}</div>
      <div className="mt-1 font-semibold text-slate-900">{value}</div>
    </div>
  );
}

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
function ScheduleTimeline({ schedules, orgs }) {
  // Group by day
  const byDay = {};
  for (const s of schedules) {
    if (byDay[s.day_of_week] == null) byDay[s.day_of_week] = [];
    byDay[s.day_of_week].push(s);
  }
  return (
    <div className="space-y-2" data-testid="schedule-timeline">
      {DAY_LABELS.map((label, idx) => {
        const slots = byDay[idx] || [];
        return (
          <div key={idx} className="flex items-start gap-3 text-sm" data-testid={`schedule-day-${idx}`}>
            <div className="w-12 shrink-0 text-xs font-semibold text-slate-500 pt-0.5">{label}</div>
            <div className="flex-1 flex flex-wrap gap-2">
              {slots.length === 0 && <span className="text-xs text-slate-400">Off</span>}
              {slots.map((s, i) => (
                <div key={i} className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs">
                  <span className="font-mono font-semibold text-slate-700">{s.start_time}–{s.end_time}</span>
                  <span className="text-slate-500"> · {orgs[s.org_id]?.name || "Clinic"}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Tile({ label, value, accent, testid }) {
  const colors = {
    sky: "from-sky-50 to-sky-100 text-sky-700 border-sky-200",
    emerald: "from-emerald-50 to-emerald-100 text-emerald-700 border-emerald-200",
    amber: "from-amber-50 to-amber-100 text-amber-700 border-amber-200",
  };
  return (
    <div className={`rounded-xl border p-4 text-center bg-gradient-to-br ${colors[accent]}`} data-testid={testid}>
      <div className="text-[10px] tracking-[0.2em] uppercase font-semibold opacity-80">{label}</div>
      <div className="font-heading text-2xl sm:text-3xl font-bold mt-1">{value}</div>
    </div>
  );
}

function BookDialog({ doctor, org, onBooked }) {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", age: "", mobile: "", gender: "", idProofType: "", idProofNumber: "", abhaId: "" });
  const [abhaStep, setAbhaStep] = useState("input"); // "input" or "otp"
  const [aadhaar, setAadhaar] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const sendOtp = async () => {
    if (!aadhaar || aadhaar.length !== 12) return toast.error("Invalid Aadhaar");
    setOtpSending(true);
    try {
      await axios.post(`${API}/booking/verify-aadhaar`, { aadhaar_number: aadhaar });
      setAbhaStep("otp");
      toast.success("OTP sent to your registered mobile");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send OTP");
    } finally {
      setOtpSending(false);
    }
  };

  const verifyOtp = async () => {
    if (!otp || otp.length !== 6) return toast.error("Invalid OTP");
    setOtpVerifying(true);
    try {
      const { data } = await axios.post(`${API}/booking/verify-otp`, { 
        aadhaar_number: aadhaar, 
        otp: otp 
      });
      setForm({ ...form, abhaId: data.abha_id });
      setAbhaStep("input");
      setAadhaar("");
      setOtp("");
      toast.success("ABHA ID created successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid OTP");
    } finally {
      setOtpVerifying(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/public/book`, {
        doctor_id: doctor.id, name: form.name, age: parseInt(form.age), mobile: form.mobile,
        lang: lang === "hi" ? "hi" : "en",
        gender: form.gender || undefined,
        id_proof_type: form.idProofType,
        id_proof_number: form.idProofNumber,
        abha_id: form.abhaId,
      });
      // remember doctor id for "back" on cancel/failure
      sessionStorage.setItem(`upchaar_book_doctor_${data.appointment.id}`, doctor.id);
      sessionStorage.setItem(`upchaar_book_${data.appointment.id}`, JSON.stringify({
        upi_link: data.upi_link, upi_vpa: data.upi_vpa, amount: data.amount,
      }));
      if (data.requires_payment) {
        // NOTE: token slip is NOT generated yet — patient must pay first.
        toast.message("Awaiting payment", { description: "Complete UPI payment to confirm your booking." });
        navigate(`/patient/book/${data.appointment.id}/pay`);
      } else {
        // Pay-at-counter fallback (provider has no UPI configured)
        toast.success(t("booking_confirmed"));
        onBooked(data.appointment);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="w-full h-12 text-base bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="patient-open-book-btn">
          {t("book_appointment")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{t("book_appointment")}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label>{t("your_name")}</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="patient-book-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("your_age")}</Label>
              <Input type="number" min="0" max="120" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} required data-testid="patient-book-age" />
            </div>
            <div>
              <Label>{t("your_mobile")}</Label>
              <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })} required minLength={10} maxLength={10} data-testid="patient-book-mobile" />
            </div>
          </div>
          <div>
            <Label className="mb-1.5 block">{t("gender") || "Gender"} <span className="text-slate-400 font-normal">(optional)</span></Label>
            <div className="flex items-center gap-2 flex-wrap">
              {[
                { v: "male", label_en: "Male", label_hi: "Purush" },
                { v: "female", label_en: "Female", label_hi: "Mahila" },
                { v: "other", label_en: "Other", label_hi: "Anya" },
              ].map((g) => (
                <button type="button" key={g.v}
                  onClick={() => setForm({ ...form, gender: form.gender === g.v ? "" : g.v })}
                  className={`text-xs px-3 py-1.5 rounded-full border transition ${form.gender === g.v
                    ? "bg-emerald-500 text-white border-emerald-500"
                    : "bg-white text-slate-600 border-slate-200 hover:border-emerald-300"}`}
                  data-testid={`patient-book-gender-${g.v}`}>
                  {lang === "hi" ? g.label_hi : g.label_en}
                </button>
              ))}
            </div>
          </div>
          {/* ID Proof Type */}
          <div>
            <Label>{t("id_proof_type") || "ID Proof Type"} <span className="text-red-500">*</span></Label>
            <select value={form.idProofType} onChange={(e) => setForm({ ...form, idProofType: e.target.value })} required className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500">
              <option value="">Select ID Type</option>
              <option value="aadhar">Aadhar</option>
              <option value="pan">PAN Card</option>
              <option value="voting">Voting Card</option>
              <option value="janaadhar">Janaadhar</option>
              <option value="driving">Driving License</option>
            </select>
          </div>
          {/* ID Proof Number */}
          <div>
            <Label>{t("id_proof_number") || "ID Proof Number"} <span className="text-red-500">*</span></Label>
            <Input value={form.idProofNumber} onChange={(e) => { let val = e.target.value; const limits = { aadhar: 12, pan: 10, voting: 10, janaadhar: 12, driving: 20 }; const maxLen = limits[form.idProofType] || 20; if (val.length <= maxLen) setForm({ ...form, idProofNumber: val }); }} required placeholder="Enter ID number" />
          </div>
          {/* ABHA ID - with Aadhaar OTP toggle */}
          <div>
            {form.abhaId ? (
              <div>
                <Label>✅ ABHA ID linked</Label>
                <div className="bg-emerald-50 p-3 rounded-lg text-sm font-mono">
                  {form.abhaId}
                </div>
                <button type="button" 
                  onClick={() => setForm({ ...form, abhaId: "" })}
                  className="text-xs text-slate-500 mt-2 underline">
                  Remove & use Aadhaar instead
                </button>
              </div>
            ) : (
              <div>
                <Label>ABHA ID / Aadhaar *</Label>
                {abhaStep === "input" ? (
                  <div className="space-y-2">
                    <Input 
                      type="text"
                      placeholder="Enter 12-digit Aadhaar"
                      value={aadhaar}
                      onChange={(e) => setAadhaar(e.target.value.replace(/\D/g, "").slice(0, 12))}
                      maxLength={12}
                      required
                    />
                    <Button 
                      type="button"
                      onClick={sendOtp}
                      disabled={otpSending || aadhaar.length !== 12}
                      className="w-full bg-slate-500 hover:bg-slate-600"
                    >
                      {otpSending ? "Sending..." : "Send OTP"}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Input 
                      type="text"
                      placeholder="Enter 6-digit OTP"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      maxLength={6}
                      required
                    />
                    <Button 
                      type="button"
                      onClick={verifyOtp}
                      disabled={otpVerifying || otp.length !== 6}
                      className="w-full bg-emerald-500 hover:bg-emerald-600"
                    >
                      {otpVerifying ? "Verifying..." : "Verify & Create ABHA"}
                    </Button>
                    <button 
                      type="button"
                      onClick={() => { setAbhaStep("input"); setOtp(""); }}
                      className="text-xs text-slate-500 underline"
                    >
                      Back to Aadhaar entry
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="rounded-lg bg-slate-50 border p-3 text-xs text-slate-600">
            {t("amount")}: <span className="font-semibold text-slate-900">₹{doctor.consultation_fee}</span>
            {org?.upi_vpa ? <> · {t("upi_id")}: <span className="font-mono">{org.upi_vpa}</span></> : null}
          </div>
          <DialogFooter>
            <Button type="submit" disabled={submitting} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="patient-book-submit">
              {submitting ? "..." : t("pay_and_book")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
